import React, { useState } from "react";
import {
    AppBar,
    Box,
    Drawer,
    IconButton,
    List,
    Toolbar
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from 'prop-types';
import { motion } from "framer-motion";
import { useNavigation } from "../../context/NavigationContext";
import { getNavigationMenuItems, navigateToPathOrSection, getLogo } from "../../utils/navigationUtils";
import MobileNavigationItem from "./MobileNavigationItem";

/**
 * Enhanced NavigationMenuMobile component with improved design and animations
 * Based on the OctopusSync template but adapted for SpinnakerAlgo
 */
const NavigationMenuMobile = ({ 
    textColor = '#000000',
    primaryColor = '#6511af',
    pageSectionRefsContainer,
    isHomePage = false
}) => {
    const {
        isMobileMenuOpen,
        setIsMobileMenuOpen,
        startPageTransition
    } = useNavigation();
    const navigate = useNavigate();
    const location = useLocation();
    const [expandedItems, setExpandedItems] = useState({});

    const menuItems = getNavigationMenuItems();
    const logoSrc = getLogo(textColor);

    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

    const handleNavigate = (targetPath, sectionId = null) => {
        const currentRefs = pageSectionRefsContainer 
            ? pageSectionRefsContainer.current 
            : {};
        navigateToPathOrSection(
            sectionId,
            targetPath,
            currentRefs,
            navigate,
            location.pathname,
            startPageTransition
        );
        setIsMobileMenuOpen(false); // Close menu after navigation
    };

    const toggleExpanded = (title) => {
        setExpandedItems(prev => ({ ...prev, [title]: !prev[title] }));
    };

    return (
        <>
            {/* Fixed App Bar with Menu Button */}
            <AppBar
                position="fixed"
                sx={{
                    display: { xs: 'flex', md: 'none' },
                    background: isHomePage 
                        ? 'rgba(0, 0, 0, 0.3)' 
                        : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    boxShadow: isHomePage ? 'none' : '0 2px 10px rgba(0,0,0,0.1)',
                    zIndex: 1300
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px !important' }}>
                    {/* Logo */}
                    {logoSrc && (
                        <Box
                            component="img"
                            src={logoSrc}
                            alt="Spinnaker Algo"
                            sx={{
                                height: 40,
                                width: 'auto'
                            }}
                        />
                    )}

                    {/* Menu Button */}
                    <IconButton
                        onClick={toggleMobileMenu}
                        aria-label="Open navigation menu"
                        sx={{
                            color: textColor,
                            padding: 1.5,
                            '&:hover': {
                                backgroundColor: isHomePage 
                                    ? 'rgba(255, 255, 255, 0.1)' 
                                    : 'rgba(0, 0, 0, 0.1)',
                                transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Navigation Drawer */}
            <Drawer
                anchor="right"
                open={isMobileMenuOpen}
                onClose={toggleMobileMenu}
                PaperProps={{
                    sx: {
                        width: "100%",
                        maxWidth: { xs: "100%", sm: "350px" },
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        color: '#000000',
                        display: "flex",
                        flexDirection: "column",
                        boxSizing: 'border-box',
                    }
                }}
                sx={{ zIndex: 1400 }}
            >
                {/* Drawer Header */}
                <Box 
                    sx={{ 
                        display: "flex", 
                        justifyContent: "space-between", 
                        alignItems: "center", 
                        padding: 3,
                        borderBottom: '1px solid rgba(0,0,0,0.05)'
                    }}
                >
                    {logoSrc && (
                        <Box
                            component="img"
                            src={logoSrc}
                            alt="Spinnaker Algo"
                            sx={{
                                height: 45,
                                width: 'auto'
                            }}
                        />
                    )}

                    <IconButton
                        onClick={toggleMobileMenu}
                        sx={{
                            color: '#000000',
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.05)',
                                transform: 'rotate(90deg)'
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Navigation List */}
                <Box sx={{ flex: 1, overflowY: 'auto', padding: 2 }}>
                    <List sx={{ padding: 0 }}>
                        {menuItems.map((item, index) => (
                            <motion.div
                                key={item.title}
                                initial={{ x: 50, opacity: 0 }}
                                animate={{ 
                                    x: 0, 
                                    opacity: 1,
                                    transition: {
                                        delay: index * 0.1,
                                        duration: 0.3,
                                        ease: [0.4, 0.0, 0.2, 1]
                                    }
                                }}
                                exit={{ 
                                    x: 50, 
                                    opacity: 0,
                                    transition: { duration: 0.2 }
                                }}
                            >
                                <MobileNavigationItem
                                    item={item}
                                    expanded={expandedItems[item.title] || false}
                                    toggleExpanded={toggleExpanded}
                                    handleNavigate={handleNavigate}
                                    location={location}
                                    activeColor={primaryColor}
                                />
                            </motion.div>
                        ))}
                    </List>
                </Box>
            </Drawer>
        </>
    );
};

NavigationMenuMobile.propTypes = {
    textColor: PropTypes.string,
    primaryColor: PropTypes.string,
    pageSectionRefsContainer: PropTypes.object,
    isHomePage: PropTypes.bool
};

export default NavigationMenuMobile;
